import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import SignaturePadLib, { type PointGroup } from 'signature_pad';

export interface SignaturePadHandle {
  clear: () => void;
  isEmpty: () => boolean;
  getDataURL: () => string;
}

interface SignaturePadProps {
  onChange?: (dataUrl: string) => void;
  className?: string;
}

const SignaturePad = forwardRef<SignaturePadHandle, SignaturePadProps>(function SignaturePad(
  { onChange, className },
  ref,
) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const padRef = useRef<SignaturePadLib | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;

    const pad = new SignaturePadLib(canvas, {
      penColor: '#111827',
      backgroundColor: '#ffffff',
      minWidth: 0.8,
      maxWidth: 2.6,
    });
    padRef.current = pad;

    const resize = () => {
      const rect = wrap.getBoundingClientRect();
      const cssW = Math.max(1, Math.floor(rect.width));
      const cssH = Math.max(1, Math.floor(rect.height));
      const ratio = Math.max(1, window.devicePixelRatio || 1);

      const data: PointGroup[] = pad.toData();
      canvas.width = cssW * ratio;
      canvas.height = cssH * ratio;
      canvas.style.width = `${cssW}px`;
      canvas.style.height = `${cssH}px`;
      const ctx = canvas.getContext('2d');
      ctx?.scale(ratio, ratio);
      pad.clear();
      if (data.length) pad.fromData(data);
    };

    resize();

    const handleEnd = () => {
      onChangeRef.current?.(pad.isEmpty() ? '' : pad.toDataURL('image/png'));
    };
    pad.addEventListener('endStroke', handleEnd);

    const ro = new ResizeObserver(resize);
    ro.observe(wrap);
    window.addEventListener('orientationchange', resize);

    return () => {
      pad.removeEventListener('endStroke', handleEnd);
      pad.off();
      ro.disconnect();
      window.removeEventListener('orientationchange', resize);
    };
  }, []);

  useImperativeHandle(ref, () => ({
    clear: () => {
      padRef.current?.clear();
      onChangeRef.current?.('');
    },
    isEmpty: () => padRef.current?.isEmpty() ?? true,
    getDataURL: () => {
      const pad = padRef.current;
      if (!pad || pad.isEmpty()) return '';
      return pad.toDataURL('image/png');
    },
  }));

  return (
    <div ref={wrapRef} className={`signature-pad-wrap ${className ?? ''}`}>
      <canvas ref={canvasRef} className="signature-pad-canvas" style={{ touchAction: 'none' }} />
    </div>
  );
});

export default SignaturePad;
